import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  Assignment,
  AssignmentCreateRequest,
  AssignmentRemoveRequest,
} from "../types";

const KEY = (participationId: string) =>
  ["participation_investigators", participationId] as const;

export function useParticipationInvestigators(participationId: string | undefined) {
  return useQuery<Assignment[]>({
    queryKey: participationId ? KEY(participationId) : ["participation_investigators", "none"],
    queryFn: () =>
      apiRequest(`/api/participations/${participationId}/investigators`),
    enabled: Boolean(participationId),
  });
}

export function useAddAssignment(participationId: string) {
  const qc = useQueryClient();
  return useMutation<Assignment, Error, AssignmentCreateRequest>({
    mutationFn: (payload) =>
      apiRequest(`/api/participations/${participationId}/investigators`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(participationId) });
      qc.invalidateQueries({ queryKey: ["investigators"] });
    },
  });
}

export function useRemoveAssignment(participationId: string) {
  const qc = useQueryClient();
  return useMutation<
    Assignment,
    Error,
    { assignmentId: string; payload: AssignmentRemoveRequest }
  >({
    mutationFn: ({ assignmentId, payload }) =>
      apiRequest(
        `/api/participations/${participationId}/investigators/${assignmentId}`,
        { method: "DELETE", body: JSON.stringify(payload) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(participationId) });
      qc.invalidateQueries({ queryKey: ["investigators"] });
    },
  });
}
